// Count / value formatting for insights — grouped thousands via Intl, plus the optional unit affix.

import type { DisplayUnit } from '../types';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');

/** A plain grouped count, e.g. `134,509`. */
export function formatCount(n: number): string {
  return NUMBER_FORMAT.format(n);
}

/** A numeric value with its optional unit affix, e.g. `$2,400` or `30%`. */
export function formatValue(n: number, unit?: DisplayUnit): string {
  const text = formatMagnitude(n);
  if (!unit) return text;
  return unit.position === 'prefix' ? `${unit.value}${text}` : `${text}${unit.value}`;
}

/** Grouped integer for whole / large numbers; up to 4 significant figures for a sub-integer value, so a
 *  decimal column (`0.00042`, `0.5126`) keeps its precision on the axis instead of rounding to `0` / `1`. */
function formatMagnitude(n: number): string {
  if (Number.isInteger(n) || Math.abs(n) >= 1000) return NUMBER_FORMAT.format(Math.round(n));
  return String(Number(n.toPrecision(4)));
}

/**
 * A share as a percentage, e.g. `26%`, guarded at BOTH ends so rounding can never turn a near-miss into
 * an absolute.
 *
 * Sub-1% collapses to `<1%` so a real-but-tiny share never reads as a confident `0%`. The top end is the
 * same rule and matters more: 99.84% rounds to `100%`, and a flat 100% is read as "all of them" — which
 * is a claim about completeness nobody measured. `>99%` says the true thing instead.
 */
export function formatPercent(pct: number): string {
  if (pct <= 0) return '0%';
  if (pct < 1) return '<1%';
  if (pct < 100 && Math.round(pct) >= 100) return '>99%';
  return `${Math.round(pct)}%`;
}
